import React, { FC, useState, useEffect } from 'react';
import { updateSenior, deleteSenior } from '../../lib/supabase';

interface EditSeniorModalProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  profileType?: string;
  severity?: string;
  deviceName?: string;
  onSeniorUpdated?: () => void;
  onSeniorDeleted?: () => void;
}

export const EditSeniorModal: FC<EditSeniorModalProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  profileType,
  severity,
  deviceName,
  onSeniorUpdated,
  onSeniorDeleted,
}) => {
  const [name, setName] = useState(seniorName);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(seniorName);
      setError(null);
      setConfirmDelete(false);
    }
  }, [isOpen, seniorName]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const getProfileDisplayName = (type?: string) => {
    const names: Record<string, string> = {
      cognitive: 'Memory & Confusion',
      physical: 'Physical & Mobility',
      visual: 'Vision',
      custom: 'Custom',
    };
    return type ? names[type] || 'Standard' : 'Not set';
  };

  const getSeverityLabel = (s?: string) => {
    if (!s) return 'Not assessed';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required.');
      return;
    }

    if (trimmed === seniorName) {
      onClose();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await updateSenior(seniorId, { name: trimmed });
      onSeniorUpdated?.();
      onClose();
    } catch (err) {
      console.error('Failed to update senior:', err);
      setError('Failed to update. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);

    try {
      await deleteSenior(seniorId);
      onSeniorDeleted?.();
      onClose();
    } catch (err) {
      console.error('Failed to delete senior:', err);
      setError('Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !saving && !deleting) {
      handleSave();
    }
  };

  const busy = saving || deleting;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-brand-text/50 backdrop-blur-sm animate-fadeIn"
        onClick={busy ? undefined : onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto animate-slideUp"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-brand-border">
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-brand-text">
                Edit Senior
              </h2>
              <p className="text-sm text-brand-text-muted mt-1">
                Update details for <span className="font-semibold text-brand-text">{seniorName}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={busy}
              className="p-2 rounded-lg hover:bg-brand-bg-warm transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <span className="material-symbols-outlined text-2xl text-brand-text-muted">close</span>
            </button>
          </div>

          {/* Body */}
          <div className="p-4 sm:p-6 space-y-4">
            {/* Name field */}
            <div>
              <label htmlFor="senior-name" className="block text-sm font-semibold text-brand-text mb-1.5">
                Name
              </label>
              <input
                id="senior-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={busy}
                className="w-full px-3 py-2.5 rounded-lg border border-brand-border bg-white text-brand-text placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors disabled:opacity-50"
                placeholder="Enter senior's name"
                autoFocus
              />
            </div>

            {/* Read-only info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-brand-bg-warm">
                <span className="text-[10px] uppercase text-brand-text-muted font-semibold tracking-wide block mb-1">
                  Profile
                </span>
                <span className="text-sm font-semibold text-brand-text">
                  {getProfileDisplayName(profileType)}
                </span>
              </div>
              <div className="p-3 rounded-lg bg-brand-bg-warm">
                <span className="text-[10px] uppercase text-brand-text-muted font-semibold tracking-wide block mb-1">
                  Severity
                </span>
                <span className="text-sm font-semibold text-brand-text">
                  {getSeverityLabel(severity)}
                </span>
              </div>
            </div>

            {deviceName && (
              <div className="p-3 rounded-lg bg-brand-bg-warm">
                <span className="text-[10px] uppercase text-brand-text-muted font-semibold tracking-wide block mb-1">
                  Device
                </span>
                <span className="text-sm font-semibold text-brand-text">{deviceName}</span>
              </div>
            )}

            {/* Delete section */}
            <div className="pt-3 border-t border-brand-border">
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                >
                  <span className="material-symbols-outlined text-lg">delete</span>
                  Remove this senior
                </button>
              ) : (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm font-semibold text-red-700 mb-1">
                    Delete {seniorName}?
                  </p>
                  <p className="text-xs text-red-600 mb-3">
                    This will permanently remove this senior and all their configuration, contacts, and assessment data. This cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      disabled={deleting}
                      className="flex-1 py-2 px-3 text-xs font-semibold text-brand-text-light bg-white border border-brand-border rounded-lg hover:bg-brand-bg-warm transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 py-2 px-3 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      {deleting ? (
                        <>
                          <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-sm">delete_forever</span>
                          Yes, Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="px-4 sm:px-6 py-3 bg-red-50 border-t border-red-100">
              <p className="text-sm text-red-600 flex items-center gap-2">
                <span className="material-symbols-outlined text-lg">error</span>
                {error}
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-brand-border bg-brand-bg-warm/50 rounded-b-2xl">
            <button
              onClick={onClose}
              disabled={busy}
              className="px-4 sm:px-6 py-2.5 text-sm font-semibold text-brand-text-light hover:text-brand-text hover:bg-brand-bg-warm rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={busy || !name.trim() || name.trim() === seniorName}
              className="flex items-center gap-2 px-4 sm:px-6 py-2.5 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {saving ? (
                <>
                  <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                  Saving...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg">save</span>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditSeniorModal;
